import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, ArrowLeft, RotateCcw } from 'lucide-react';

const VerifyEmail = () => {
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'expired'
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    const verifyToken = async () => {
      // Get token from URL params
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token provided');
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/?login=true');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Email verification failed');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        setMessage('An error occurred during email verification');
      }
    };

    verifyToken();
  }, [location.search, navigate, API_BASE]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleResendEmail = async () => {
    // This would need to be implemented if we want a resend option
    // For now, just direct users to contact support or try registering again
    alert('Please try registering again or contact support if you continue to have issues.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-videbits-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
        <div className="mb-6">
          {status === 'loading' && (
            <div className="flex flex-col items-center">
              <RotateCcw className="w-16 h-16 text-videbits-600 animate-spin mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email</h1>
              <p className="text-gray-600">Please wait while we verify your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">You'll be redirected to the login page in a few seconds...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center">
              <XCircle className="w-16 h-16 text-red-500 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              
              <div className="space-y-3 w-full">
                <button
                  onClick={handleResendEmail}
                  className="w-full bg-videbits-600 text-white py-3 px-4 rounded-lg hover:bg-videbits-700 transition-colors font-medium flex items-center justify-center"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Get Help
                </button>
                
                <button
                  onClick={handleGoHome}
                  className="w-full bg-videbits-100 text-videbits-700 py-3 px-4 rounded-lg hover:bg-videbits-200 transition-colors font-medium flex items-center justify-center"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Go to Homepage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
